<template>
  <div class="select" ref="select" :style="selectStyle">
    <div class="select__header"
         :class="{'select__header-border': !borderLess, 'select__header_shifted': shiftLabelRight,
         'select__header_highlighted': isOpened && !isNotHighlightOnOpen, 'select__disabled': disabled}"
         :style="headerStyle"
         @click="open()">
    <span v-if="selectedOption"
          class="select__selection"
          :class="{'select__selection_center': alignTitleCenter, 'select__selection_align-left': alignLeft}"
          :style="titleStyle"
          :title="selectedOption.label">
          <base-truncate-label v-if="truncateLabel"
                               v-bind:private-content="selectedOption.attributes?.some(attr=> attr['private-content'])"
                               :label="selectedOption.label"
                               watch-class="select__selection" />
          <template v-else>
            <span
            v-bind:private-content="selectedOption.attributes?.some(attr=> attr['private-content'])"
            >{{selectedOption.label}}</span>
          </template>
      </span>
      <span v-else
            class="select__placeholder"
            :class="{'select__placeholder_error': isNotValid, 'select__placeholder_with-icon': placeholderIcon}"
            :style="{'color': headerStyle && headerStyle.color, ...placeholderStyle}"
      >
        <base-material-design-icon :icon="placeholderIcon" v-if="placeholderIcon" />
        {{placeholder}}</span>
      <slot></slot>
      <span v-if="!isHideArrow && !isGreyArrow"
            class="select__arrow"
            :class="{
              'select__disabled-arrow': disabled,
              'select__arrow-grey': isGreyArrowColor,
              'select__arrow-rotated': isRotation && isOpened
            }"
      ></span>
      <div v-if="isGreyArrow" class="select__grey-arrow"></div>
    </div>
    <template v-if="isOpened">
      <div class="select__overlay"
           @click.stop="close()"></div>
      <div class="select__input-container" 
          @click.stop="()=>{}" 
          v-if="isSmartInput"
          :style="{'width': listWidth + 'px'}">
        <input class="select__input" :style="smartInputStyle" type="text" v-model="searchInput">
        <div class="select__clear-button" @click.stop="searchInput = ''" v-if="searchInput"></div>
        <base-material-design-icon class="select__search-button" icon="search" v-else></base-material-design-icon>
      </div>
      <ul class="select__list"
          :class="[{'select__list_shadow': listItemBoxShadow},'select__list_' + listPosition]"
          :style="{ ...listStyle, ...propListStyle }"
          ref="list">
        <li class="select__item"
            v-for="(option, index) in filteredOptions"
            :style="{...propListItemStyle, ...(option.style || {})}"
            :key="index"
            :class="{'select__item_selected': option.value === modelValue,
                     'select__item_disabled': disabledOption(option)}"
            :title="option.label"
            @click.stop="select(option)">
          <slot name="type" v-bind:option="option"></slot>
          <base-truncate-label v-if="truncateLabel"
                               :style="option.style || {}"
                               :label="option.label"
                               v-bind:private-content="option.attributes?.some(attr=> attr['private-content'])"
                               :shrink="1"
                               watch-class="select__item" />
          <span v-else
            v-bind:private-content="option.attributes?.some(attr=> attr['private-content'])"
          >
            {{option.label}}
          </span>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'

/**
 * General component for all dropdowns
 */
export default {
  name: 'BaseSelect',
  data () {
    return {
      isOpened: false,
      listStyle: {},
      /**
       * top or bottom. Set in {@link #_setListStyle} method
       * */
      listPosition: '',
      searchInput: '',
      listWidth: 0,
      resizeObserver: new ResizeObserver(this.handleListResize)
    }
  },
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  mounted () {
    window.addEventListener('resize', _.debounce(() => {
      this.setListStyle()
    }, 50), false)
  },
  props: {
    /**
     * option format: {
     *   label: String,
     *   value: String
     * }
     * */
    options: {
      type: Array,
      default: () => []
    },
    propListStyle: {
      type: Object,
      default: () => ({})
    },
    propListItemStyle: {
      type: Object,
      default: () => ({})
    },
    /* placeholder for input */
    placeholder: {
      type: String,
      default: ''
    },
    placeholderIcon: {
      type: String,
      default: null
    },
    // selected options
    modelValue: {
      default: null
    },
    // using truncate logic for label
    truncateLabel: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showSelectedOnTop: {
      type: Boolean,
      default: false
    },
    isRotation: {
      type: Boolean,
      default: false
    },
    isNotHighlightOnOpen: {
      type: Boolean,
      default: false
    },
    isSortAsc: {
      type: Boolean,
      default: false
    },
    isSmartInput: {
      type: Boolean,
      default: false
    },
    isHideArrow: {
      type: Boolean,
      default: false
    },
    isGreyArrow: {
      type: Boolean,
      default: false
    },
    isGreyArrowColor: {
      type: Boolean,
      default: false
    },
    maxHeightList: {
      type: Number
    },
    /* is used for external validation */
    isNotValid: {
      type: Boolean,
      default: false
    },
    /* all props below are used for changing design of component */
    borderLess: {
      type: Boolean,
      default: false
    },
    // dropdown shadow
    listItemBoxShadow: {
      type: Boolean,
      default: false
    },
    shiftLabelRight: {
      type: Boolean,
      default: false
    },
    // align title to the left
    alignLeft: {
      type: Boolean,
      default: false
    },
    // identify advanced options
    isAdvancedOptions: {
      type: Boolean,
      default: false
    },
    // align title to the center
    alignTitleCenter: {
      type: Boolean,
      default: false
    },
    // show top border on dropdown
    showTopBorder: {
      type: Boolean,
      default: false
    },
    /* all props below are used for set styles outside the component  */
    selectStyle: {
      type: Object,
      default: null
    },
    headerStyle: {
      type: Object,
      default: null
    },
    smartInputStyle: {
      type: Object,
      default: null
    },
    titleStyle: {
      type: Object,
      default: null
    },
    placeholderStyle: {
      type: Object,
      default: () => {}
    },
  },
  watch: {
    isOpened (v) {
      this.$emit('toggle', v)
      this.$nextTick(() => {
        if (this.$refs.list) {
          this.resizeObserver.observe(this.$refs.list)
        } else {
          this.resizeObserver.disconnect()
        }
      })
    }
  },
  computed: {
    selectedOption () {
      return this.options.find(option => option.value === this.modelValue)
    },
    filteredOptions () {
      let options = [...this.options]
      if (this.isSmartInput) {
        options = options.filter(i => i.label.toLowerCase().includes(this.searchInput.toLowerCase()))
      }
      if (this.isSortAsc) {
        options = options.sort((a, b) => (a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0))
      }
      const modelValue = this.modelValue
      return !this.showSelectedOnTop
        ? options
        : options.sort((a, b) => a.value === modelValue ? -1 : b.value === modelValue ? 1 : 0)
    }
  },
  methods: {
    open () {
      if (!this.disabled) {
        this.isOpened = true
        this.setListStyle()
      }
    },
    disabledOption (option) {
      if (this.isAdvancedOptions &&
        !option.operators.options.length &&
        option.disableWithoutOptions) {
        return true
      } else {
        return false
      }
    },
    close () {
      this.searchInput = ''
      this.isOpened = false
    },
    select (option) {
      const isOptionDisabled = this.disabledOption(option)
      if (option.value === this.modelValue || isOptionDisabled) {
        return
      }
      this.close()
      this.$emit('change', option.value)
    },
    /**
     * @alias _setListStyle
     * creates stylized dropdown list
     */
    setListStyle () {
      const ref = this.$refs[ 'select' ]
      if (!ref) {
        return
      }
      const style = {}
      const { top, bottom, left, right } = ref.getBoundingClientRect()
      const bottomShift = window.innerHeight - bottom
      const itemsHeight = this.options.length * 30
      let maxHeight, verticalProp
      if (bottomShift >= itemsHeight || (bottomShift > top && bottomShift < itemsHeight)) {
        verticalProp = 'top'
        maxHeight = Math.round(bottomShift) - 60
      } else if (itemsHeight <= top || (bottomShift < top && top < itemsHeight)) {
        verticalProp = 'bottom'
        maxHeight = Math.round(top) - 60
      }
      if (!this.isSmartInput) {
        style[verticalProp] = '100%'
      }
      maxHeight = this.maxHeightList ? this.maxHeightList : maxHeight
      style[ 'max-height' ] = `${maxHeight}px`
      if (verticalProp === 'top') {
        this.showTopBorder && (style[ 'border-top' ] = '2px solid #284aff')
        style[ 'border-radius' ] = '0 0 8px 8px'
      } else {
        this.showTopBorder && (style[ 'border-bottom' ] = '2px solid #284aff')
        style[ 'border-radius' ] = '8px 8px 0 0'
      }
      const leftShift = left
      const rightShift = window.innerWidth - right
      const horizontalProp = rightShift < leftShift ? 'right' : 'left'
      style[ horizontalProp ] = 0
      this.listStyle = style
      this.listPosition = verticalProp
    },
    handleListResize() {
      if (!this.$refs.list) {
        return
      }
      this.listWidth = this.$refs.list.getBoundingClientRect().width
    }
  }
}
</script>

<style scoped lang="scss">

  .select {
    position: relative;
    user-select: none;
    cursor: default;
    height: 40px !important;
    &__grey-arrow {
      margin: auto 0 auto 8px;
      width: 0;
      height: 0;
      display: inline-block;
      border-style: solid;
      border-width: 4px 4.5px 0 4.5px;
      border-color: #70757a transparent transparent transparent;
    }
    &__header {
      display: flex;
      height: 100%;
      background-color: transparent;
      box-sizing: border-box;
      cursor: pointer;

      &-border {
        border: 1px solid $rfx-light-steel;
        border-radius: 4px;
      }
      &_shifted {
        padding-left: 10px;
      }
      &_highlighted {
        background-color: rgba(40, 74, 255, 0.08);
      }
    }
    &__selection,
    &__placeholder {
      display: flex;
      align-items: center;
      height: 100%;
      flex: 1 1 100%;
      font-size: 14px;
      padding-left: 20px;
      box-sizing: border-box;
      @include ellipsis;

      &_with-icon {
        padding-left: 30px;
      }
    }
    &__selection {
      color: inherit;
      &_center {
        display: flex;
        justify-content: center;
      }
      &_align-left {
        padding-left: 0;
      }
    }
    &__placeholder {
      color: $rfx-silver;
      font-weight: normal;
      &_error {
        color: $rfx-light-red;
      }
    }
    &__disabled {
      pointer-events: none;
    }
    &__arrow {
      flex: 0 0 40px;
      display: flex;
      align-items: center;
      justify-content: center;

      &-rotated {
        transform: rotate(180deg);
      }

      &::after {
        content: '';
        display: block;
        width: 7px;
        height: 7px;
        box-sizing: border-box;
        border-right: 2px solid $rfx-bright-blue;
        border-bottom: 2px solid $rfx-bright-blue;
        transform: rotate(45deg);
        transform-origin: center;
      }

      &-grey {
        &::after {
          border-color: #2f404e !important;
        }
      }
    }
    &__disabled-arrow {
      background-color: $rfx-almost-white-lighter;
      display: none;
    }
    &__input-container {
      position: relative;
      display: flex;
      margin: 0;
      z-index: 99998;
      border-bottom: 1.8pt solid blue;
      box-shadow: 0 0 1px 0 rgba(8, 8, 8, .24), 0 4px 15px 0 rgba(8, 8, 8, .24);
    }
    &__input {
      border: none;
      height: 20px;
      flex: 1 1 100%;
      padding: 10px 35px 10px 20px;
      width: 100%;
      outline: none;
    }

    &__clear-button,
    &__search-button {
      position: absolute;
      top: 10px;
      right: 7px;
      width: 22px;
      height: 22px;
      background-color: transparent;
      color: $rfx-dark-march;
    }
    &__clear-button {
      cursor: pointer;
      &:hover {
        opacity: 1;
      }

      &:before, &:after {
        position: absolute;
        left: 7px;
        top: 3px;
        content: '';
        height: 12px;
        width: 2px;
        background-color: currentColor;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9998;
    }

    &__list {
      position: absolute;
      z-index: 9999;
      min-width: 100%;
      max-width: 600px;
      padding: 0;
      background: #fff;
      list-style-type: none;
      overflow-x: hidden;
      overflow-y: auto;
      margin: 0 0 1px;

      &_top {
        box-shadow: 0 15px 40px -20px rgba(0, 0, 0, .3);
      }
      &_bottom {
        box-shadow: 0 -15px 40px -20px rgba(0, 0, 0, .3);
      }
      &_shadow {
        box-shadow: 0 0 1px 0 rgba(8, 8, 8, .24), 0 4px 15px 0 rgba(8, 8, 8, .24);
      }
    }

    &__item {
      height: 30px;
      line-height: 30px;
      padding: 0 20px;
      @include ellipsis;
      font-size: 12px;
      color: #000;
      display: flex;
      align-items: center;
      &_selected {
        color: #fff !important;
        background-color: $rfx-bright-blue;

        div {
          color: #fff !important;
        }
      }
      &_disabled{
        color: $rfx-silver;
      }
      &:hover:not(.select__item_selected):not(.select__item_disabled) {
        color: #000 !important;
        background-color: rgba(1,22,39,.1);
      }
    }
  }
</style>
